import { take } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AddPropertyModalComponent } from '@clients/components/modals/add-property-modal/add-property-modal.component';
import { EditPropertyModalComponent } from '@clients/components/modals/edit-property-modal/edit-property-modal.component';
import { IClientForm } from '@clients/models/domain/client.domain';
import { IClientProperty } from '@clients/models/firebase/client.model';
import { ClientsService } from '@clients/services/clients/clients.service';
import { PhoneNumberControlComponent } from '@shared/components/forms/phone-number-control/phone-number-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { TextareaControlComponent } from '@shared/components/forms/textarea-control/textarea-control.component';
import { AccordionItemComponent } from '@shared/components/ui/accordion-item/accordion-item.component';
import { AccordionComponent } from '@shared/components/ui/accordion/accordion.component';
import { ToastTypes } from '@shared/models/toast';
import { PropertyPipe } from '@shared/pipes/property.pipe';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-mobile-client-edit-details',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		AccordionComponent,
		AccordionItemComponent,
		StandardControlComponent,
		TextareaControlComponent,
		PhoneNumberControlComponent,
		PropertyPipe,
	],
	templateUrl: './mobile-client-edit-details.component.html',
	styleUrl: './mobile-client-edit-details.component.scss',
})
export class MobileClientEditDetailsComponent {
	@Input() clientForm: FormGroup<IClientForm>;
	@Input() properties: Array<IClientProperty>;
	@Input() clientId: string;
	@Output() propertyChange: EventEmitter<void> = new EventEmitter<void>();
	private modalService = inject(ModalService);
	private toastService = inject(ToastService);
	private clientsService = inject(ClientsService);

	openAddProperty(): void {
		const modalRef = this.modalService.open(AddPropertyModalComponent);
		const componentInstance = modalRef.componentInstance as AddPropertyModalComponent;
		componentInstance.propertySelected.pipe(take(1)).subscribe((property: IClientProperty) => {
			this.clientsService.addClientProperty(this.clientId, property).then(() => {
				this.toastService.showToast({
					title: 'Client',
					message: 'Added client property',
					type: ToastTypes.SUCCESS,
				});
			});
			this.propertyChange.emit();
		});
	}

	openEditProperty(clientProperty: IClientProperty): void {
		const modalRef = this.modalService.open(EditPropertyModalComponent);
		const componentInstance = modalRef.componentInstance as EditPropertyModalComponent;
		componentInstance.clientProperty = clientProperty;
		componentInstance.propertySelected.pipe(take(1)).subscribe((property: IClientProperty) => {
			this.clientsService.editClientProperty(this.clientId, property).then(() => {
				this.toastService.showToast({
					title: 'Client',
					message: 'Updated client property',
					type: ToastTypes.SUCCESS,
				});
			});
			this.propertyChange.emit();
		});
		componentInstance.propertyRemoved.pipe(take(1)).subscribe((propertyId: string) => {
			this.clientsService.removeClientProperty(this.clientId, propertyId).then(() => {
				this.toastService.showToast({
					title: 'Client',
					message: 'Removed client property',
					type: ToastTypes.SUCCESS,
				});
			});
			this.propertyChange.emit();
		});
	}
}
